import { readAnalyticsRecord, readAnalyticsString } from './analytics-chart-data';

export type AnalyticsDisplayField = {
  key?: string;
  label?: string | null;
  unit?: string | null;
  visible?: boolean | null;
  format?: string | null;
  valueFormat?: string | null;
};

export type AnalyticsDisplayTableColumn = AnalyticsDisplayField & {
  key: string;
};

const GENERIC_HIDDEN_COLUMNS = new Set(['__typename']);

export function humanizeAnalyticsLabel(value: string) {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/^./, (firstLetter) => firstLetter.toUpperCase());
}

function readDisplayRecord(spec: unknown) {
  return readAnalyticsRecord(readAnalyticsRecord(spec).display);
}

function readDisplayField(value: unknown): AnalyticsDisplayField | null {
  const stringLabel = readAnalyticsString(value);
  if (stringLabel) {
    return { label: stringLabel };
  }

  const record = readAnalyticsRecord(value);
  if (Object.keys(record).length === 0) {
    return null;
  }

  return {
    label: readAnalyticsString(record.label),
    unit: readAnalyticsString(record.unit),
    visible: typeof record.visible === 'boolean' ? record.visible : null,
    format: readAnalyticsString(record.format),
    valueFormat: readAnalyticsString(record.valueFormat),
  };
}

function readDisplayFields(spec: unknown, collectionKey: 'fields' | 'series') {
  return readAnalyticsRecord(readDisplayRecord(spec)[collectionKey]);
}

function readTableSpec(spec: unknown) {
  return readAnalyticsRecord(readAnalyticsRecord(spec).table);
}

function readTableColumn(value: unknown): AnalyticsDisplayTableColumn | null {
  const record = readAnalyticsRecord(value);
  const key = readAnalyticsString(record.key);
  if (!key) {
    return null;
  }

  return {
    key,
    label: readAnalyticsString(record.label),
    unit: readAnalyticsString(record.unit),
    visible: typeof record.visible === 'boolean' ? record.visible : null,
    format: readAnalyticsString(record.format),
    valueFormat: readAnalyticsString(record.valueFormat),
  };
}

export function readAnalyticsTableColumns(spec: unknown) {
  const columns = readTableSpec(spec).columns;
  if (!Array.isArray(columns)) {
    return [];
  }

  return columns
    .map(readTableColumn)
    .filter((column): column is AnalyticsDisplayTableColumn => Boolean(column));
}

export function getAnalyticsFieldDisplay(spec: unknown, key: string) {
  return readDisplayField(readDisplayFields(spec, 'fields')[key]);
}

export function getAnalyticsSeriesDisplay(spec: unknown, key: string) {
  return (
    readDisplayField(readDisplayFields(spec, 'series')[key]) ?? getAnalyticsFieldDisplay(spec, key)
  );
}

export function getAnalyticsFieldLabel(spec: unknown, key: string, fallback?: string | null) {
  return getAnalyticsFieldDisplay(spec, key)?.label ?? fallback ?? humanizeAnalyticsLabel(key);
}

export function getAnalyticsSeriesLabel(spec: unknown, key: string) {
  return getAnalyticsSeriesDisplay(spec, key)?.label ?? getAnalyticsFieldLabel(spec, key);
}

export function getAnalyticsSeriesUnit(spec: unknown, key: string) {
  return getAnalyticsSeriesDisplay(spec, key)?.unit ?? getAnalyticsFieldDisplay(spec, key)?.unit;
}

export function getAnalyticsValueDisplayLabel(
  spec: unknown,
  value: unknown,
  fieldKey?: string | null
) {
  const rawValue =
    typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
      ? String(value)
      : '';
  if (!rawValue) {
    return '-';
  }

  const values = readAnalyticsRecord(readDisplayRecord(spec).values);
  const fieldValues = fieldKey ? readAnalyticsRecord(values[fieldKey]) : {};
  const fieldLabel = readAnalyticsString(fieldValues[rawValue]);
  if (fieldLabel) {
    return fieldLabel;
  }

  const flatLabel = readAnalyticsString(values[rawValue]);
  return flatLabel;
}

export function getAnalyticsValueLabel(spec: unknown, value: unknown, fieldKey?: string | null) {
  const rawValue =
    typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
      ? String(value)
      : '';
  if (!rawValue) {
    return '-';
  }

  return getAnalyticsValueDisplayLabel(spec, value, fieldKey) ?? humanizeAnalyticsLabel(rawValue);
}

export function getDisplayTableColumns(
  spec: unknown,
  rows: Array<Record<string, unknown>>
): AnalyticsDisplayTableColumn[] {
  const configuredColumns = readAnalyticsTableColumns(spec);
  if (configuredColumns.length > 0) {
    return configuredColumns.filter((column) => column.visible !== false);
  }

  return Array.from(new Set(rows.flatMap((row) => Object.keys(row))))
    .filter((column) => !GENERIC_HIDDEN_COLUMNS.has(column))
    .sort((a, b) => a.localeCompare(b))
    .map((key) => ({
      key,
      label: getAnalyticsFieldLabel(spec, key),
    }));
}
